/* eslint-disable no-case-declarations */
/* eslint-disable no-useless-escape */
import * as luhn from 'luhn';
import address from '@sideway/address';

import { validationMessages } from './validationMessages';

/**
 * @access private
 * @param {string} digits
 * @return number new document CNPJ to test of validation
 */
function calcDigit(digits, initialWeight) {
  let sum = 0;
  let weight = initialWeight;

  for (let i = 0; i < digits.length; i++) {
    sum += digits[i] * weight;
    weight--;
    if (weight < 2) weight = 9;
  }

  const remainder = sum % 11;
  return remainder < 2 ? 0 : 11 - remainder;
}

function validCalc(x, numbers) {
  const slice = numbers.slice(0, x);
  let factor = x - 7;
  let sum = 0;

  for (let i = x; i >= 1; i--) {
    const n = slice[x - i];
    sum += n * factor--;
    if (factor < 2) factor = 9;
  }

  const result = 11 - (sum % 11);

  return result > 9 ? 0 : result;
}

function matchNumbers(value = '') {
  const match = value.toString().match(/\d/g);
  return Array.isArray(match) ? match.map(Number) : [];
}

function testCnpj(value) {
  if (!value) return false;

  // Verifica se é uma string ou número
  const isString = typeof value === 'string';
  const isNumber = Number.isInteger(value);

  if (!isString && !isNumber) return false;

  const stringValue = isString ? value : value.toString();

  // Retorna false se a entrada contiver letras
  if (/[a-zA-Z]/.test(stringValue)) return false;

  // Remove caracteres não numéricos
  const digitsOnly = stringValue.replace(/\D/g, '');

  // Verifica se o valor tem exatamente 14 dígitos
  if (digitsOnly.length !== 14) return false;

  // Extrai os números do valor
  const numbers = matchNumbers(digitsOnly);

  // Verifica se todos os dígitos são iguais
  const items = [...new Set(numbers)];
  if (items.length === 1) return false;

  // Calcula os dois dígitos verificadores
  const digits = numbers.slice(12);
  const digit0 = validCalc(12, numbers);
  if (digit0 !== digits[0]) return false;

  const digit1 = validCalc(13, numbers);
  return digit1 === digits[1];
}

/**
 * @access private
 * @param {string} value
 * @return bool true if valid or false not valid
 */
export const testCpf = (value) => {
  const matchCPF = value.match(/\d{3}.\d{3}.\d{3}-\d{2}?/);
  if (matchCPF == null) return false;

  let rest = 0;
  let sum = 0;
  const strCPF = value.replace(/\D/g, '');
  if (
    strCPF === '' ||
    strCPF === '00000000000' ||
    strCPF === '11111111111' ||
    strCPF === '22222222222' ||
    strCPF === '33333333333' ||
    strCPF === '44444444444' ||
    strCPF === '55555555555' ||
    strCPF === '66666666666' ||
    strCPF === '77777777777' ||
    strCPF === '88888888888' ||
    strCPF === '99999999999'
  )
    return false;

  for (let i = 1; i <= 9; i++)
    sum += parseInt(strCPF.substring(i - 1, i)) * (11 - i);
  rest = (sum * 10) % 11;

  if (rest == 10 || rest == 11) rest = 0;
  if (rest != parseInt(strCPF.substring(9, 10))) return false;

  sum = 0;
  for (let i = 1; i <= 10; i++)
    sum += parseInt(strCPF.substring(i - 1, i)) * (12 - i);
  rest = (sum * 10) % 11;

  if (rest == 10 || rest == 11) rest = 0;
  if (rest != parseInt(strCPF.substring(10, 11))) return false;
  return true;
};

/**
 * @access private
 * @param {string} value
 * @return bool true if valid or false not valid
 */
export const testDni = (value) => {
  if (!value) return false;

  // Remove espaços e caracteres especiais
  const strDNI = value.replace(/\D/g, '');

  // DNI deve ter exatamente 8 dígitos
  if (strDNI.length !== 8) return false;

  // Não pode ser todos os dígitos iguais
  if (
    strDNI === '00000000' ||
    strDNI === '11111111' ||
    strDNI === '22222222' ||
    strDNI === '33333333' ||
    strDNI === '44444444' ||
    strDNI === '55555555' ||
    strDNI === '66666666' ||
    strDNI === '77777777' ||
    strDNI === '88888888' ||
    strDNI === '99999999'
  ) {
    return false;
  }

  // Verifica se todos os caracteres são dígitos
  if (!/^\d+$/.test(strDNI)) return false;

  return true;
};

export function inputValidate(type, inputValue, lang = 'pt-BR', length = null) {
  let valid = true;
  let errorMessage = null;
  const messages = validationMessages[lang];

  switch (type) {
    case 'NAME':
      const safeValue = inputValue ? inputValue?.trim() : '';
      const arrayNome = safeValue.split(' ');
      const matchName = safeValue.match(
        /^([a-zA-ZàèìòùÀÈÌÒÙáéíóúýÁÉÍÓÚÝâêîôûÂÊÎÔÛãñõÃÑÕäëïöüÿÄËÏÖÜŸçÇ']{1,}(\s|$)){2,}$/
      );
      if (arrayNome[0].length > 40) {
        errorMessage = messages.NAME.tooLong;
        valid = false;
      }

      if (arrayNome.length < 2) {
        errorMessage = messages.NAME.incomplete;
        valid = false;
      } else if (matchName == null) {
        valid = false;
        errorMessage = messages.NAME.format;
      }
      break;

    case 'EMAIL':
      const value = inputValue;
      const matchEmail = value.match(
        /^(([^<>'"`*%(()\[\]\\.,;:\s@"]+(\.[^<>'"`*%()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,3}))$/
      );
      const matchAccentEmail = value.match(
        /([àèìòùÀÈÌÒÙáéíóúýÁÉÍÓÚÝâêîôûÂÊÎÔÛãñõÃÑÕäëïöüÿÄËÏÖÜŸçÇ])+/
      );

      const prefix = value.substring(0, value.indexOf('@'));
      const posfix = value.substring(
        value.indexOf('@') + 1,
        value.indexOf('.')
      );

      if (
        matchEmail == null ||
        matchAccentEmail != null ||
        prefix.length < 2 ||
        posfix.length < 2 ||
        value.length < 7 ||
        value.length > 80 ||
        !address.email.isValid(value)
      ) {
        valid = false;
        errorMessage = messages.EMAIL.format;
      }
      break;

    case 'PHONE':
      const hasDDI = inputValue.indexOf('+') > -1;

      if (hasDDI) {
        if (inputValue.length < 6) {
          valid = false;
          errorMessage = messages.PHONE.format;
        }
      } else {
        if (lang === 'es-PE') {
          const peruLocalPattern = /^[9][0-9]{8}$/;
          const numbersOnly = inputValue?.replace(/\D/g, '');

          if (!peruLocalPattern.test(numbersOnly)) {
            valid = false;
            errorMessage = messages.PHONE.format;
          }
        } else {
          const matches = inputValue.match(
            /^(([1-9]{2})\s?([9]{1})([0-9]{4})[-]([0-9]{4}))$/
          );
          if (matches == null) {
            valid = false;
            errorMessage = messages.PHONE.format;
          }
        }
      }
      break;

    case 'CARD_NUMBER':
      const matcheCardNumber = inputValue.match(
        /(\d{4}\s?\d{4}\s?\d{4}\s?\d{4})|(\d{4}\s?\d{6}\s?\d{5})|(\d{4}\s?\d{6}\s?\d{4})/
      );
      if (!luhn.validate(inputValue) || matcheCardNumber == null) {
        valid = false;
        errorMessage = messages.CARD_NUMBER.format;
      }
      break;

    case 'CARD_EXPIRE_DATE':
      const expNum = inputValue?.replace(/\D/g, '');
      const mes = expNum.substring(0, 2);
      let ano = expNum.substring(2, 6);
      const hoje = new Date();
      let mesAtual = hoje.getUTCMonth() + 1;
      const anoAtual = hoje.getUTCFullYear();
      if (mesAtual < 10) mesAtual = `0${mesAtual}`;
      if (ano.length == 2) ano = `20${ano}`;
      if (
        mes > '12' ||
        ano < anoAtual ||
        (ano == anoAtual && mes < mesAtual) ||
        ano > anoAtual + 10
      ) {
        valid = false;
        errorMessage = messages.CARD_EXPIRE_DATE.format;
      }
      break;

    case 'REGISTRY':
      const numbers = inputValue?.replace(/\D/g, '');

      if (numbers?.length === 11 && !testCpf(inputValue)) {
        valid = false;
        errorMessage = messages.REGISTRY.format;
      } else if (numbers?.length === 14 && !testCnpj(inputValue)) {
        errorMessage = messages.REGISTRY.format;
        valid = false;
      } else if (numbers?.length !== 14 && numbers?.length !== 11) {
        valid = false;
        errorMessage = messages.REGISTRY.format;
      }
      break;

    case 'CPF':
      if (!inputValue) {
        errorMessage = messages.CPF.format;
        valid = false;
      } else if (!testCpf(inputValue)) {
        errorMessage = messages.CPF.format;
        valid = false;
      }
      break;

    case 'CNPJ':
      if (!inputValue || !testCnpj(inputValue)) {
        errorMessage = messages.CNPJ.format;
        valid = false;
      }
      break;

    case 'DNI':
      if (!inputValue) {
        errorMessage = messages.DNI.format;
        valid = false;
      } else if (!testDni(inputValue)) {
        errorMessage = messages.DNI.format;
        valid = false;
      }
      break;

    case 'ADDRESS':
      const matchEnd = inputValue.match(
        /^[a-zA-Z0-9À-ú.]+( [a-zA-Z0-9À-ú.]+)*$/
      );
      if (matchEnd == null) {
        valid = false;
      }
      if (!valid) errorMessage = messages.ADDRESS.format;
      break;

    case 'BAIRRO':
      const matchBairro = inputValue.match(
        /^[a-zA-Z0-9À-ú]+( [a-zA-Z0-9À-ú]+)*$/
      );
      if (matchBairro == null) {
        valid = false;
      }

      if (!valid) errorMessage = messages.BAIRRO.format;
      break;

    case 'ESTADO':
      if (inputValue == '') {
        valid = false;
        errorMessage = messages.ESTADO.format;
      }
      break;

    case 'CIDADE':
      const matchCidade = inputValue.match(
        /^[a-zA-Z0-9À-ú]+( [a-zA-Z0-9À-ú]+)*$/
      );
      if (matchCidade == null) {
        valid = false;
      }

      if (!valid) errorMessage = messages.CIDADE.format;
      break;

    case 'PAIS':
      const matchPais = inputValue.match(
        /^[a-zA-Z0-9À-ú]+( [a-zA-Z0-9À-ú]+)*$/
      );
      if (matchPais == null) {
        valid = false;
      }

      if (!valid) errorMessage = messages.PAIS.format;
      break;

    case 'CEP':
      const matchCEP = inputValue.match(/\d{2}\d{3}-\d{3}?/);
      if (matchCEP == null) {
        valid = false;
      }

      if (!valid) errorMessage = messages.CEP.format;
      break;

    case 'NUMERO':
      if (inputValue == '') {
        valid = false;
        errorMessage = messages.NUMERO.format;
      }
      break;

    case 'COMPLEMENTO':
      const matchComp = inputValue.match(
        /^[a-zA-Z0-9À-ú]*( [a-zA-Z0-9À-ú]+)*$/
      );
      if (matchComp == null) {
        valid = false;
      }

      if (!valid) errorMessage = messages.COMPLEMENTO.format;
      break;

    case 'CARD_CVV':
      const matchCVV = inputValue.match(/[0-9][0-9][0-9][0-9]?/);

      /**
       * @todo
       * será ajustado para os demais contextos posteriormente
       * a princípio sanará o WOL para evitar possíveis problemas
       */

      const hasLengthValidation = !!length;
      const isCVVLengthValid = inputValue.length === Number(length);

      if (matchCVV == null || (hasLengthValidation && !isCVVLengthValid)) {
        valid = false;
        errorMessage =
          lang === 'pt-BR'
            ? 'Revise o código de segurança.'
            : 'Revise o código de segurança';
      }
      break;

    case 'CARD_COMPANY':
      if (!inputValue) {
        errorMessage = 'Revise a bandeira do cartão.';
        valid = false;
      }
  }

  return { valid, errorMessage };
}
